import axios, { AxiosResponse } from 'axios';

import { HSCAN_BACKEND_API_URL } from '../../env';
import { NonOptionalKeys } from '../../utils';
import { CodedEntry } from '../../utils/bodyPart';
import { Page } from '../page';

import { AddressInfo } from './address';
import { CdDeliveryPayment } from './hospital';
import { PageQueryParams, validateUploadParamValue } from './types';

export interface Series {
  seriesNumber: string;
  seriesInstanceUID: string;
  seriesDescription: string;
  images: string[];
}
/**
 * Hscan 에 저장된 의료 영상. DICOM 들은 HPACS 에 저장되어있다.
 *
 * Case 는 Study 이다.
 */
export interface Case {
  userId: string;
  ownerId: string;
  caseId: string;
  patientId: string;
  birthDate?: string;
  patientName?: string;
  patientSex?: string;
  studyDate?: string;
  accessionNumber?: string;
  studyInstanceUID?: string;
  studyDescription?: string;
  modality: string;
  bodyPart?: string[];
  series?: Series[];
  institutionName?: string;
  contentIds: string[];
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  requestedDate?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  acceptedDate?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  createdAt: string;
}

const DEFAULT_CASE_PAGE_SIZE = 5;

export interface FilterText {
  filter: string;
  type: string;
}
export interface FilterDate {
  date: string;
  date2?: string;
  type: string;
}
export interface FilterSet {
  values: string[];
}
interface Filter {
  filterId?: string;
  filterName?: string;
  userId?: string;
  name?: FilterText;
  birthDate?: FilterDate;
  bodypart?: FilterText;
  gender?: FilterSet;
  studyDescription?: FilterText;
  studyDate?: FilterDate;
  modalities?: FilterSet;
  general?: FilterText;
}
export type FilterContent = Omit<Filter, 'filterId' | 'filterName' | 'userId'>;

// export interface RequestCaseQueryParams extends PageQueryParams, FilterContent {} // 가 맞는 거 아닌가
export interface RequestCaseQueryParams extends PageQueryParams {
  filter?: FilterContent;
}
export const requestCase = (params: RequestCaseQueryParams) => {
  const { filter, page, size = DEFAULT_CASE_PAGE_SIZE } = params;
  return axios.get<Page<Case>>(`${HSCAN_BACKEND_API_URL}/case`, {
    params: {
      ...filter,
      page,
      size,
    },
  });
};

export interface RequestCaseByIdsQueryParams {
  ids: string[];
}
export const requestCaseByIds = (params: RequestCaseByIdsQueryParams) => {
  return axios.get<Case[]>(`${HSCAN_BACKEND_API_URL}/case`, {
    params,
  });
};

export interface DeleteCaseParams {
  caseIds: string[];
}
export const deleteCase = (params: DeleteCaseParams) => {
  return axios.delete<
    void,
    AxiosResponse<void, DeleteCaseParams>,
    DeleteCaseParams
  >(`${HSCAN_BACKEND_API_URL}/case`, {
    params,
  });
};

export interface GenerateHpacsLinkBody {
  caseId: string[];
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  startDtime?: string;
  /**
   * yyyy-MM-ddTHH:mm:ss.SSSZ
   */
  endDtime?: string;
}
export interface HpacsLinkResponse {
  /**
   * {HPACS_URL}/linkLogin/{id}
   */
  id: string;
  // linkObject: RequestHpacsLinkBody;
}
/**
 * HPACS link 를 생성
 */
export const generateHpacsLinkId = (body: GenerateHpacsLinkBody) => {
  return axios.post<
    HpacsLinkResponse,
    AxiosResponse<HpacsLinkResponse>,
    GenerateHpacsLinkBody
  >(`${HSCAN_BACKEND_API_URL}/link`, body);
};

export interface ShareCode {
  /**
   * 6자리 숫자로 이루어진 string
   */
  code: string;
  // request: RequestHpacsLinkBody;
  // linkResponse: HpacsLinkResponse;
}
export const generateShareCode = (linkRequest: GenerateHpacsLinkBody) => {
  return axios.post<ShareCode, AxiosResponse<ShareCode>, GenerateHpacsLinkBody>(
    `${HSCAN_BACKEND_API_URL}/share-code`,
    linkRequest,
  );
};
interface RequestLinkByCodeParams {
  /**
   * 6자리 숫자로 이루어진 string
   */
  code: string;
}
export const requestLinkByCode = ({ code }: RequestLinkByCodeParams) => {
  return axios.get<HpacsLinkResponse>(
    `${HSCAN_BACKEND_API_URL}/share-code/${code}`,
  );
};

export interface UploadProgress {
  loaded: number;
  total?: number;
}
export interface UploadDicomsBody {
  name: string;
  birthDate: string;
  gender: string;
}
export const uploadDicomMandatoryKeys: NonOptionalKeys<UploadDicomsBody>[] = [
  'name',
  'birthDate',
  'gender',
];
export const validateUploadDicomsBody = (
  params: Partial<UploadDicomsBody>,
): params is UploadDicomsBody => {
  return uploadDicomMandatoryKeys.every(key =>
    validateUploadParamValue(params[key]),
  );
};

export const uploadDicoms = (
  body: UploadDicomsBody,
  files: File[],
  onProgress?: (p: UploadProgress) => void,
) => {
  const form = new FormData();

  files.forEach(file => form.append('files', file));
  const blob = new Blob([JSON.stringify(body)], {
    type: 'application/json',
  });
  form.append('patient', blob);

  return axios.post<Case[], AxiosResponse<Case[]>, FormData>(
    `${HSCAN_BACKEND_API_URL}/case/dicom`,
    form,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
      timeout: 1000 * 60 * 30,
      onUploadProgress: onProgress,
    },
  );
};

export interface UploadImagesBody {
  patientId: string;
  patientName: string;
  patientGender: string;
  birthDate: string;
  symptom: string;
  injuryLocation: CodedEntry;
  studyDate: string;
  institutionName?: string;
  description?: string;
}
export const uploadImagesMandatoryKeys: NonOptionalKeys<UploadImagesBody>[] = [
  'patientId',
  'patientName',
  'patientGender',
  'birthDate',
  'symptom',
  'injuryLocation',
  'studyDate',
];
export const validateUploadImagesBody = (
  form: Partial<UploadImagesBody>,
): form is UploadImagesBody => {
  return uploadImagesMandatoryKeys.every(key =>
    validateUploadParamValue(form[key]),
  );
};

export const uploadImages = (
  body: UploadImagesBody,
  files: File[],
  onProgress?: (p: UploadProgress) => void,
) => {
  const formData = new FormData();

  files.forEach(file => formData.append('files', file));
  const formBlob = new Blob([JSON.stringify(body)], {
    type: 'application/json',
  });
  formData.append('form', formBlob);

  return axios.post<Case[], AxiosResponse<Case[]>, FormData>(
    `${HSCAN_BACKEND_API_URL}/case/form`,
    formData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
      timeout: 1000 * 60 * 30,
      onUploadProgress: onProgress,
    },
  );
};

export const requestDicomImage = (imageId: string) => {
  return axios.get<File>(`${HSCAN_BACKEND_API_URL}/image/${imageId}`, {
    responseType: 'blob',
  });
};

export const requestDownloadMultipleCases = (ids: string[]) => {
  return axios.get<File>(`${HSCAN_BACKEND_API_URL}/case/dicom`, {
    headers: {
      'Content-Type': 'application/zip',
    },
    responseType: 'blob',
    params: {
      ids,
    },
  });
};

export interface DicomDownloadEvent {
  /** DOWNLOAD | COMPRESS | COMPLETE */
  eventId: 'DOWNLOAD' | 'COMPRESS' | 'COMPLETE';
  /** 전체 검사 수 */
  totalCount: number;
  /** 전체 이미지 수 */
  totalImageCount: number;
  /** 완료된 검사 수 */
  completeCount: number;
  /** 완료된 이미지 수 */
  completeImageCount: number;
}
/** 다운로드 이벤트 스트림 시작 */
export const requestStartEventStream = (sseKey: string) => {
  return axios.get<DicomDownloadEvent>(
    `${HSCAN_BACKEND_API_URL}/case/dicom/sse/${sseKey}/event`,
    {
      responseType: 'stream',
    },
  );
};

export const requestDownloadImage = (sseKey: string, caseIds: string[]) => {
  return axios.get<File>(`${HSCAN_BACKEND_API_URL}/case/dicom/sse/${sseKey}`, {
    headers: {
      'Content-Type': 'application/zip',
    },
    responseType: 'blob',
    params: { ids: caseIds },
  });
};

export interface Report {
  plainText: string;
}
export const requestReport = (caseId: string) => {
  return axios.get<Report>(`${HSCAN_BACKEND_API_URL}/case/${caseId}/report`);
};

export interface CdDeliveryRequestBody {
  caseIds: string[];
  mailingAddress: AddressInfo;
  deliveryFee: number;
}
export const requestPay = (body: CdDeliveryRequestBody) => {
  return axios.post<
    CdDeliveryPayment,
    AxiosResponse<CdDeliveryPayment>,
    CdDeliveryRequestBody
  >(`${HSCAN_BACKEND_API_URL}/cd-delivery/payment`, body);
};

export interface CdDeliveryVerifyParams {
  paymentId: string;
}
export const verifyPayment = ({ paymentId }: CdDeliveryVerifyParams) => {
  return axios.put(
    `${HSCAN_BACKEND_API_URL}/cd-delivery/payment/${paymentId}`,
    undefined,
  );
};
